/**
 * Character limit validation utility for Customer Request Form
 * Provides validation and alert functionality for form fields with character limits
 */

export const CHARACTER_LIMITS = {
  requestorName: 50,
  fullCustomerName: 200,
  picContact: 50,
  emailAddress: 500,
  businessName: 200,
  streetAddress: 200,
  cityStateZip: 100,
  workToBePerformed: 100,
  problemDescription: 4000,
  carsonComments: 4000,
  modelCode: 50,
  personInCharge: 100
};

export const FIELD_LABELS = {
  requestorName: 'Requestor Name',
  fullCustomerName: 'Full Customer Name',
  picContact: 'PIC/Contact',
  emailAddress: 'E-Mail Address',
  businessName: 'Business Name',
  streetAddress: 'Street Address',
  cityStateZip: 'City, State, Zipcode',
  workToBePerformed: 'Work to be Performed',
  problemDescription: 'Problem Description and Comments',
  carsonComments: 'Carson Comments',
  modelCode: 'Model Code',
  personInCharge: 'Person In Charge'
};

/**
 * Validates character limits for a single field
 * @param {string} fieldName - The field name to validate
 * @param {string} value - The field value
 * @returns {object} Validation result with isValid, message, and remaining characters
 */
export const validateFieldCharacterLimit = (fieldName, value) => {
  const limit = CHARACTER_LIMITS[fieldName];
  const fieldLabel = FIELD_LABELS[fieldName] || fieldName;
  
  if (!limit) {
    return { isValid: true, message: '', remainingChars: null };
  }
  
  const currentLength = value ? value.length : 0;
  const remainingChars = limit - currentLength;
  const isValid = currentLength <= limit;
  
  let message = '';
  if (!isValid) {
    message = `${fieldLabel} exceeds the maximum limit of ${limit} characters by ${Math.abs(remainingChars)} characters.`;
  } else if (remainingChars <= 50 && remainingChars > 0) {
    message = `${fieldLabel} is approaching the limit. ${remainingChars} characters remaining.`;
  }
  
  return {
    isValid,
    message,
    remainingChars,
    currentLength,
    limit
  };
};

/**
 * Validates character limits for all form fields
 * @param {object} formData - The form data object
 * @param {array} models - Array of model objects
 * @returns {array} Array of validation errors
 */
export const validateAllCharacterLimits = (formData, models = []) => {
  const errors = [];
  
  // Validate main form fields
  Object.keys(CHARACTER_LIMITS).forEach(fieldName => {
    if (formData[fieldName] !== undefined) {
      const validation = validateFieldCharacterLimit(fieldName, formData[fieldName]);
      if (!validation.isValid) {
        errors.push({
          field: fieldName,
          message: validation.message,
          type: 'CHARACTER_LIMIT_EXCEEDED'
        });
      }
    }
  });
  
  // Validate model fields
  models.forEach((model, index) => {
    if (model.model) {
      const validation = validateFieldCharacterLimit('modelCode', model.model);
      if (!validation.isValid) {
        errors.push({
          field: `model_${index}`,
          message: `Model ${index + 1}: ${validation.message}`,
          type: 'CHARACTER_LIMIT_EXCEEDED'
        });
      }
    }
    
    if (model.personInCharge) {
      const validation = validateFieldCharacterLimit('personInCharge', model.personInCharge);
      if (!validation.isValid) {
        errors.push({
          field: `personInCharge_${index}`,
          message: `Person In Charge ${index + 1}: ${validation.message}`,
          type: 'CHARACTER_LIMIT_EXCEEDED'
        });
      }
    }
  });
  
  return errors;
};

/**
 * Gets helper text for a field showing character count and limit
 * @param {string} fieldName - The field name
 * @param {string} value - The current field value
 * @param {string} baseHelperText - Base helper text to append to
 * @returns {string} Helper text with character count
 */
export const getCharacterCountHelperText = (fieldName, value, baseHelperText = '') => {
  const limit = CHARACTER_LIMITS[fieldName];
  if (!limit) return baseHelperText;
  
  const currentLength = value ? value.length : 0;
  const countText = `${currentLength}/${limit} characters`;
  
  return baseHelperText ? `${baseHelperText} (${countText})` : countText;
};

/**
 * Determines if a field should show a warning color based on character count
 * @param {string} fieldName - The field name
 * @param {string} value - The current field value
 * @returns {boolean} True if field should show warning
 */
export const shouldShowCharacterWarning = (fieldName, value) => {
  const validation = validateFieldCharacterLimit(fieldName, value);
  return !validation.isValid || (validation.remainingChars !== null && validation.remainingChars <= 50);
};

/**
 * Gets the appropriate color for character count display
 * @param {string} fieldName - The field name
 * @param {string} value - The current field value
 * @returns {string} Color code for the character count
 */
export const getCharacterCountColor = (fieldName, value) => {
  const validation = validateFieldCharacterLimit(fieldName, value);
  
  if (!validation.isValid) {
    return 'error'; // Red for exceeded limit
  } else if (validation.remainingChars !== null && validation.remainingChars <= 50) {
    return 'warning'; // Orange for approaching limit
  }
  
  return 'text.secondary'; // Default gray
};